import React, { useRef, useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import _ from "lodash";

import {
  Button,
  Dropdown,
  Checkbox,
  Badge,
  Tag,
  Tooltip,
} from "@toddle-edu/ds-web";
import { PinOutlined, PinFilled, SettingsOutlined } from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";

import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils";
import { updateSettings } from "modules/ConfigurationThunks";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  DUE_ON,
  VISIBILITY,
  CONFIGURE_ASSIGN_SETTINGS_SEEN,
  CLOSES_ON,
  OPENS_ON,
} from "Constants/stringConstants";
import { getAdvancedTimelineSettingsOptions } from "ClassRoom/components/AddAssignment/Utils";

import classes from "./AdvancedTimelineSettings.scss";

const getUpdateSettings = (currentSettings, value) => {
  let updatedSettings = [...currentSettings];
  if (_.includes(currentSettings, value)) {
    updatedSettings = _.filter(currentSettings, key => key !== value);
  } else {
    updatedSettings.push(value);
  }
  return updatedSettings;
};

const checkHasTurnitinAttachment = (studentTemplates = []) => {
  const hasTurnitin = _.some(studentTemplates, template => {
    const attachmentGroups = _.get(template, "attachmentGroups", []);
    return _.some(attachmentGroups, group => {
      const attachments = _.get(group, "attachments", []);
      return _.some(attachments, attachment => {
        const service = _.get(attachment, "metadata.service");
        return service === "TURNITIN";
      });
    });
  });

  return hasTurnitin;
};

const getInitialPreferredSettings = () => {
  const settingValue =
    getSettingValue({
      name: "AssignmentTimelineSettings",
      readFromUserAndOrgLevel: true,
    }) || [];

  const initialSettingValue = [...settingValue];

  if (!_.includes(settingValue, CONFIGURE_ASSIGN_SETTINGS_SEEN)) {
    if (!_.includes(settingValue, VISIBILITY)) {
      initialSettingValue.push(VISIBILITY);
    }
    if (!_.includes(settingValue, DUE_ON)) {
      initialSettingValue.push(DUE_ON);
    }
  }

  return initialSettingValue;
};

const AdvancedTimelineSettings = props => {
  const {
    currentTimelineSettings,
    handleTimelineSettingsChange,
    updateSettings,
    customButtonType,
    shouldDisableClosesOnSetting = false,
    disabled = false,
    studentTemplates = [],
    testId,
  } = props;

  const { t } = useI18n();

  const itemsRef = useRef({});
  const finalPrefferedTimelineSettings = useRef([]);

  const hasTurnitin = checkHasTurnitinAttachment(studentTemplates);

  const [
    preferredTimelineSettings,
    setPreferredTimelineSettings,
  ] = useState(() => getInitialPreferredSettings());

  const options = getAdvancedTimelineSettingsOptions(t);
  const isConfigureSettingsNotSeen = !_.includes(
    preferredTimelineSettings,
    CONFIGURE_ASSIGN_SETTINGS_SEEN
  );

  useEffect(() => {
    if (hasTurnitin && !currentTimelineSettings.includes(OPENS_ON)) {
      const updatedSettings = [...currentTimelineSettings, OPENS_ON];
      handleTimelineSettingsChange(updatedSettings);
    }
  }, [hasTurnitin, currentTimelineSettings]);

  useEffect(() => {
    // If user is seeing the timeline settings for the first time, then select "VISIBILITY" and "DUE_ON" by default for current assignment
    if (isConfigureSettingsNotSeen) {
      let updatedSettings = currentTimelineSettings;
      if (!_.includes(currentTimelineSettings, VISIBILITY)) {
        updatedSettings = getUpdateSettings(
          currentTimelineSettings,
          VISIBILITY
        );
      }
      if (!_.includes(currentTimelineSettings, DUE_ON)) {
        updatedSettings = getUpdateSettings(updatedSettings, DUE_ON);
      }
      if (!_.isEqual(updatedSettings, currentTimelineSettings)) {
        handleTimelineSettingsChange(updatedSettings);
      }
    }
  }, [currentTimelineSettings]);

  useEffect(() => {
    finalPrefferedTimelineSettings.current = preferredTimelineSettings;
  }, [preferredTimelineSettings]);

  useEffect(() => {
    return () => {
      try {
        updateSettings({
          settings: [
            {
              name: "AssignmentTimelineSettings",
              value: JSON.stringify(
                finalPrefferedTimelineSettings?.current || []
              ),
            },
          ],
          showToastMsg: false,
        });
      } catch (err) {
        console.error(err);
      }
    };
  }, []);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  useEffect(() => {
    if (isDropdownVisible) {
      const timer = setTimeout(() => {
        itemsRef?.current[0]?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isDropdownVisible]);

  const handleVisibleChange = visible => {
    if (visible) {
      if (hasTurnitin && !currentTimelineSettings.includes(OPENS_ON)) {
        handleTimelineSettingsChange([...currentTimelineSettings, OPENS_ON]);
        setTimeout(() => {
          setIsDropdownVisible(true);
        }, 0);
        return;
      }

      if (isConfigureSettingsNotSeen) {
        setPreferredTimelineSettings(
          getUpdateSettings(
            preferredTimelineSettings,
            CONFIGURE_ASSIGN_SETTINGS_SEEN
          )
        );
      }
    }

    setIsDropdownVisible(visible);
  };

  const renderIcon = useMemo(() => {
    return isConfigureSettingsNotSeen ? (
      <Badge
        dsVersion="2.0"
        type="dot"
        variant="active"
        shouldAnimateActiveDotBadge={true}
      >
        <SettingsOutlined />
      </Badge>
    ) : (
      <SettingsOutlined />
    );
  }, [isConfigureSettingsNotSeen]);

  const renderOverlay = useMemo(() => {
    return (
      <div className={classes.dropdownOverlayContainer}>
        {_.map(
          options,
          ({ key, label, subText, showNewTag = false, testId }, idx) => {
            const isChecked = _.includes(currentTimelineSettings, key);
            const isPinned = _.includes(preferredTimelineSettings, key);
            const isDisabled =
              shouldDisableClosesOnSetting && key === CLOSES_ON;

            const handleCheckboxClick = () => {
              if (isDisabled) {
                return;
              }
              handleTimelineSettingsChange(
                getUpdateSettings(currentTimelineSettings, key)
              );
            };

            const handlePinIconClick = e => {
              e?.stopPropagation();

              if (!isPinned && !isChecked) {
                handleTimelineSettingsChange(
                  getUpdateSettings(currentTimelineSettings, key)
                );
              }
              setPreferredTimelineSettings(
                getUpdateSettings(preferredTimelineSettings, key)
              );
            };

            const handleCheckboxKeyDown = e => {
              switch (e?.key) {
                case "ArrowDown": {
                  e?.preventDefault();
                  const nextItem =
                    itemsRef?.current?.[(idx + 1) % _.size(options)];
                  nextItem?.focus();
                  break;
                }
                case "ArrowUp": {
                  e?.preventDefault();
                  const prevItem =
                    itemsRef?.current?.[
                      (idx - 1 + _.size(options)) % _.size(options)
                    ];
                  prevItem?.focus();
                  break;
                }
                default:
                  onEnterOrSpaceKeyDown(e, handleCheckboxClick);
              }
            };

            const handlePinIconKeyDown = e => {
              onEnterOrSpaceKeyDown(e, handlePinIconClick);
            };

            const dropdownItemClasses = classNames(classes.dropdownItem, {
              [classes.disabledDropdownItem]: isDisabled,
            });
            const labelClasses = classNames("text-label", {
              [classes.disabledText]: isDisabled,
            });
            const subTextClasses = classNames(
              "text-body",
              classes.dropdownItemSubText,
              {
                [classes.disabledText]: isDisabled,
              }
            );

            return (
              <Tooltip
                key={key}
                dsVersion="2.0"
                tooltip={t("classRoom:closes_on_setting_disabled_text")}
                showArrow={true}
                trigger={isDisabled ? ["hover"] : [null]}
              >
                <div
                  className={dropdownItemClasses}
                  onClick={handleCheckboxClick}
                  onKeyDown={handleCheckboxKeyDown}
                  tabIndex={0}
                  role="button"
                  aria-label={label}
                  ref={ref => (itemsRef.current[idx] = ref)}
                  data-test-id={testId}
                >
                  <Checkbox
                    dsVersion="2.0"
                    isChecked={isChecked}
                    disabled={isDisabled}
                  />
                  <div className={classes.dropdownItemLabelContainer}>
                    <div className={classes.labelWithTag}>
                      <div className={labelClasses}>{label}</div>
                      {showNewTag ? (
                        <Tag dsVersion="2.0" size="small" color="teal">
                          {t("common:new")}
                        </Tag>
                      ) : null}
                    </div>
                    <div className={subTextClasses}>{subText}</div>
                  </div>
                  <Tooltip
                    dsVersion="2.0"
                    tooltip={t(
                      "classRoom:timeline_settings_preference_pin_icon_tooltip"
                    )}
                    showArrow={true}
                    placement="left"
                  >
                    <div
                      className={classes.pinIcon}
                      onClick={handlePinIconClick}
                      onKeyDown={handlePinIconKeyDown}
                      tabIndex={0}
                      role="button"
                      aria-label={label}
                    >
                      {isPinned ? (
                        <PinFilled
                          style={{ color: colors.decorativeSurfaceThree }}
                        />
                      ) : (
                        <PinOutlined variant="subtle" />
                      )}
                    </div>
                  </Tooltip>
                </div>
              </Tooltip>
            );
          }
        )}
      </div>
    );
  }, [
    options,
    currentTimelineSettings,
    preferredTimelineSettings,
    shouldDisableClosesOnSetting,
    handleTimelineSettingsChange,
    t,
  ]);

  return (
    <Dropdown
      overlay={renderOverlay}
      visible={isDropdownVisible}
      onVisibleChange={handleVisibleChange}
      disabled={disabled}
    >
      <Button
        dsVersion="2.0"
        variant="neutral"
        type={customButtonType || "plain"}
        icon={renderIcon}
        testId={testId}
      >
        {t("classRoom:configure_assign_settings")}
      </Button>
    </Dropdown>
  );
};

const mapActionCreators = {
  updateSettings,
};

export default connect(null, mapActionCreators)(AdvancedTimelineSettings);
